import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Alert, AlertDescription } from '../../ui/alert';
import { useEngineeringAuth } from '../../../hooks/useEngineeringAuth';
import { EngineeringOnboarding } from './EngineeringOnboarding';
import { LogIn, UserPlus, Zap, AlertCircle } from 'lucide-react';

interface EngineeringAuthProps {
  onAuthSuccess: () => void;
}

export const EngineeringAuth: React.FC<EngineeringAuthProps> = ({ onAuthSuccess }) => {
  const { loginDemo, loginLive, registerLive, isDemoMode, isLoading } = useEngineeringAuth();
  const [activeTab, setActiveTab] = useState(isDemoMode ? 'demo' : 'live');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState({ 
    username: '', 
    email: '', 
    password: '' 
  });

  const handleDemoLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await loginDemo(credentials.username, credentials.password);
      onAuthSuccess();
    } catch (error) {
      setError('Demo login failed. Please check your credentials.');
      console.error('Demo login failed:', error);
    }
  };

  const handleLiveLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await loginLive(credentials.email, credentials.password);
      onAuthSuccess();
    } catch (error) {
      setError('Login failed. Please check your email and password.');
      console.error('Live login failed:', error);
    }
  };

  const handleSignUp = () => {
    setShowOnboarding(true);
  };

  const handleOnboardingComplete = async (userData: any) => {
    try {
      await registerLive({
        email: userData.email,
        password: userData.password,
        name: `${userData.firstName} ${userData.lastName}`,
        phoneNumber: userData.phoneNumber,
        workspaceId: userData.workspaceId,
        role: userData.role,
        preferences: userData.preferences
      });
      setShowOnboarding(false);
      onAuthSuccess();
    } catch (error) {
      setError('Registration failed. Please try again.');
      console.error('Registration failed:', error);
    }
  };

  if (showOnboarding) {
    return (
      <EngineeringOnboarding
        mode={activeTab as 'demo' | 'live'}
        onComplete={handleOnboardingComplete}
        onSkip={() => setShowOnboarding(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 sphix-background flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md"
      >
        <Card className="sphix-card-enhanced">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white flex items-center justify-center gap-2">
              <Zap className="w-6 h-6 text-purple-400" />
              Engineering Bridge
            </CardTitle>
            <p className="text-slate-400 mt-2">
              Access your engineering dashboard
            </p>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-4 bg-red-900/20 border-red-800 text-red-300">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 bg-slate-800">
                <TabsTrigger value="demo" className="text-slate-300 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                  Demo Mode
                </TabsTrigger>
                <TabsTrigger value="live" className="text-slate-300 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                  Live Mode
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="demo" className="space-y-4 mt-6">
                <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3 mb-4">
                  <p className="text-blue-300 text-sm">
                    <strong>Demo Credentials:</strong><br />
                    Username: Demo123, DemoCounty, or DemoTyler<br />
                    Password: echoecho
                  </p>
                </div>
                <form onSubmit={handleDemoLogin} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Username</Label>
                    <Input
                      value={credentials.username}
                      onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                      placeholder="Demo123, DemoCounty, or DemoTyler"
                      className="bg-slate-800 border-slate-600 text-black placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Password</Label>
                    <Input
                      type="password"
                      value={credentials.password}
                      onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                      placeholder="echoecho"
                      className="bg-slate-800 border-slate-600 text-black placeholder:text-slate-500"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isLoading || !credentials.username || !credentials.password} 
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {isLoading ? 'Signing In...' : 'Sign In (Demo)'}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="live" className="space-y-4 mt-6">
                <form onSubmit={handleLiveLogin} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      type="email"
                      value={credentials.email}
                      onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                      placeholder="your.email@company.com"
                      className="bg-slate-800 border-slate-600 text-black placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Password</Label>
                    <Input
                      type="password"
                      value={credentials.password}
                      onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                      placeholder="Your password"
                      className="bg-slate-800 border-slate-600 text-black placeholder:text-slate-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      disabled={isLoading || !credentials.email || !credentials.password} 
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </Button>
                    <Button 
                      type="button" 
                      onClick={handleSignUp} 
                      variant="outline" 
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Sign Up
                    </Button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
